import React from "react";

export const MENTION_MARKER_REGEX = /@\[([^\]]+)\]\((?:userId|userid):(\d+)\)/gi;
const APP_MENTION_GREEN = "#14A45C";
const APP_MENTION_BG = "rgba(20, 164, 92, 0.16)";
const NOTE_MENTION_DARK_TEXT = "#FFF4C7";
const NOTE_MENTION_DARK_BG = "rgba(0, 0, 0, 0.18)";
const NOTE_MENTION_LIGHT_TEXT = "#5E4700";
const NOTE_MENTION_LIGHT_BG = "rgba(94, 71, 0, 0.14)";

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

export const stripMentionMarkers = (text) => {
  if (!text) return "";
  return text.replace(MENTION_MARKER_REGEX, (_full, name) => `@${name}`);
};

export const replaceDisplayMentionsWithMarkers = (text, users = []) => {
  if (!text) {
    return "";
  }

  const sortedUsers = [...users]
    .filter((user) => user?.id && user?.name)
    .sort((left, right) => right.name.length - left.name.length);

  return sortedUsers.reduce((currentText, user) => {
    const escapedName = escapeRegExp(user.name);
    const pattern = new RegExp(`(^|\\s)@${escapedName}(?=$|\\s|[.,;:!?])`, "g");

    return currentText.replace(
      pattern,
      (fullMatch, prefix) => `${prefix}@[${user.name}](userId:${user.id})`
    );
  }, text);
};

export const syncDisplayMentionsToRaw = (
  displayText,
  currentRawText = "",
  users = []
) => {
  const knownUsers = new Map();
  const registerUser = (user) => {
    if (!user?.id || !user?.name) {
      return;
    }
    knownUsers.set(String(user.id), {
      id: user.id,
      name: user.name,
      profileImage: user.profileImage
    });
  };

  (users || []).forEach(registerUser);

  let match;
  MENTION_MARKER_REGEX.lastIndex = 0;
  // eslint-disable-next-line no-cond-assign
  while ((match = MENTION_MARKER_REGEX.exec(currentRawText || "")) !== null) {
    registerUser({
      id: Number(match[2]),
      name: match[1]
    });
  }

  return replaceDisplayMentionsWithMarkers(
    displayText,
    Array.from(knownUsers.values())
  );
};

export const getMentionDisplayRanges = (rawText) => {
  if (!rawText) {
    return [];
  }

  const ranges = [];
  let rawCursor = 0;
  let displayCursor = 0;
  let match;

  MENTION_MARKER_REGEX.lastIndex = 0;
  // eslint-disable-next-line no-cond-assign
  while ((match = MENTION_MARKER_REGEX.exec(rawText)) !== null) {
    const plainChunk = rawText.slice(rawCursor, match.index);
    displayCursor += plainChunk.length;

    const mentionText = `@${match[1]}`;
    ranges.push({
      userId: Number(match[2]),
      name: match[1],
      start: displayCursor,
      end: displayCursor + mentionText.length
    });

    displayCursor += mentionText.length;
    rawCursor = match.index + match[0].length;
  }

  return ranges;
};

export const removeMentionAtCaret = (
  rawText,
  selectionStart,
  selectionEnd,
  key,
  users = []
) => {
  if (!rawText) {
    return null;
  }

  const displayText = stripMentionMarkers(rawText);
  const ranges = getMentionDisplayRanges(rawText);

  if (!ranges.length) {
    return null;
  }

  let targetRange = null;
  let trimTrailingSpace = false;

  if (selectionStart !== selectionEnd) {
    targetRange = ranges.find(
      (range) => selectionStart < range.end && selectionEnd > range.start
    );
  } else if (key === "Backspace") {
    targetRange =
      ranges.find(
        (range) => selectionStart > range.start && selectionStart <= range.end
      ) ||
      ranges.find(
        (range) =>
          selectionStart === range.end + 1 && displayText[range.end] === " "
      );

    trimTrailingSpace =
      Boolean(targetRange) &&
      selectionStart === targetRange.end + 1 &&
      displayText[targetRange.end] === " ";
  } else if (key === "Delete") {
    targetRange = ranges.find(
      (range) => selectionStart >= range.start && selectionStart < range.end
    );

    trimTrailingSpace =
      Boolean(targetRange) && displayText[targetRange.end] === " ";
  }

  if (!targetRange) {
    return null;
  }

  const afterStart =
    targetRange.end + (trimTrailingSpace && displayText[targetRange.end] === " " ? 1 : 0);
  const nextDisplayText =
    displayText.slice(0, targetRange.start) + displayText.slice(afterStart);

  return {
    value: syncDisplayMentionsToRaw(nextDisplayText, rawText, users),
    caret: targetRange.start
  };
};

const resolveMentionColors = (variant = "default") => {
  if (variant === "note-dark") {
    return {
      color: NOTE_MENTION_DARK_TEXT,
      backgroundColor: NOTE_MENTION_DARK_BG
    };
  }

  if (variant === "note-light") {
    return {
      color: NOTE_MENTION_LIGHT_TEXT,
      backgroundColor: NOTE_MENTION_LIGHT_BG
    };
  }

  return {
    color: APP_MENTION_GREEN,
    backgroundColor: APP_MENTION_BG
  };
};

const renderMentionCaret = (key, className) => (
  <span
    key={key}
    className={className}
    data-testid="mention-input-caret"
    style={{
      display: "inline-block",
      width: 0,
      height: "1.2em",
      borderLeft: "1px solid currentColor",
      verticalAlign: "-0.18em",
      pointerEvents: "none"
    }}
  />
);

const shouldRenderCaret = (caretPosition) =>
  Number.isFinite(caretPosition) && caretPosition >= 0;

const pushTextWithCaret = ({
  parts,
  text,
  displayStart,
  caretPosition,
  caretClassName,
  keyPrefix,
  caretInserted
}) => {
  const displayEnd = displayStart + text.length;

  if (
    !caretInserted.value &&
    shouldRenderCaret(caretPosition) &&
    caretPosition >= displayStart &&
    caretPosition <= displayEnd
  ) {
    const offset = caretPosition - displayStart;
    if (offset > 0) {
      parts.push(text.slice(0, offset));
    }
    parts.push(renderMentionCaret(`${keyPrefix}-caret`, caretClassName));
    if (offset < text.length) {
      parts.push(text.slice(offset));
    }
    caretInserted.value = true;
    return;
  }

  if (text) {
    parts.push(text);
  }
};

const renderMentionHighlight = (
  match,
  key,
  variant = "default",
  caretOptions = {}
) => {
  const { color, backgroundColor } = resolveMentionColors(variant);
  const mentionText = `@${match[1]}`;
  const innerParts = [];
  pushTextWithCaret({
    parts: innerParts,
    text: mentionText,
    displayStart: caretOptions.displayStart ?? -1,
    caretPosition: caretOptions.caretPosition,
    caretClassName: caretOptions.caretClassName,
    keyPrefix: `${key}-mention`,
    caretInserted: caretOptions.caretInserted || { value: true }
  });

  return (
  <span
    key={key}
    style={{
      color,
      fontWeight: 600,
      backgroundColor,
      borderRadius: 3,
      boxShadow: `0 0 0 2px ${backgroundColor}`
    }}
  >
    {innerParts.length ? innerParts : mentionText}
  </span>
  );
};

export const renderMentionDisplayRanges = (displayText, ranges, options = {}) => {
  const caretPosition = options.caretPosition;
  const caretClassName = options.caretClassName;
  const caretInserted = { value: false };
  const parts = [];
  let cursor = 0;
  (ranges || []).forEach((range, index) => {
    const plainText = displayText.slice(cursor, range.start);
    pushTextWithCaret({ parts, text: plainText, displayStart: cursor, caretPosition, caretClassName, keyPrefix: `group-${index}`, caretInserted });
    const name = displayText.slice(range.start + 1, range.end);
    parts.push(renderMentionHighlight([null, name], `group-${index}-mention`, options.variant || "default", {
      displayStart: range.start, caretPosition, caretClassName, caretInserted
    }));
    cursor = range.end;
  });
  pushTextWithCaret({ parts, text: displayText.slice(cursor), displayStart: cursor, caretPosition, caretClassName, keyPrefix: "group-end", caretInserted });
  if (!caretInserted.value && shouldRenderCaret(caretPosition) && caretPosition >= displayText.length) {
    parts.push(renderMentionCaret("group-end-caret", caretClassName));
  }
  return parts;
};

export const renderMentionInputText = (rawText, options = {}) => {
  const caretPosition = options.caretPosition;
  const caretClassName = options.caretClassName;
  if (!rawText) {
    return shouldRenderCaret(caretPosition)
      ? [renderMentionCaret("empty-caret", caretClassName)]
      : null;
  }
  const variant = options.variant || "default";
  const caretInserted = { value: false };

  const parts = [];
  let lastIndex = 0;
  let displayIndex = 0;
  let match;

  MENTION_MARKER_REGEX.lastIndex = 0;
  // eslint-disable-next-line no-cond-assign
  while ((match = MENTION_MARKER_REGEX.exec(rawText)) !== null) {
    if (match.index > lastIndex) {
      const plainText = rawText.slice(lastIndex, match.index);
      pushTextWithCaret({
        parts,
        text: plainText,
        displayStart: displayIndex,
        caretPosition,
        caretClassName,
        keyPrefix: `${lastIndex}-${match.index}`,
        caretInserted
      });
      displayIndex += plainText.length;
    }

    const mentionText = `@${match[1]}`;
    parts.push(
      renderMentionHighlight(match, `${match.index}-${match[2]}`, variant, {
        displayStart: displayIndex,
        caretPosition,
        caretClassName,
        caretInserted
      })
    );
    displayIndex += mentionText.length;
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < rawText.length) {
    const plainText = rawText.slice(lastIndex);
    pushTextWithCaret({
      parts,
      text: plainText,
      displayStart: displayIndex,
      caretPosition,
      caretClassName,
      keyPrefix: `${lastIndex}-end`,
      caretInserted
    });
    displayIndex += plainText.length;
  }

  if (
    !caretInserted.value &&
    shouldRenderCaret(caretPosition) &&
    caretPosition >= displayIndex
  ) {
    parts.push(renderMentionCaret("end-caret", caretClassName));
  }

  return parts;
};

export const renderMentionText = (text, options = {}) => {
  if (!text) return null;
  const variant = options.variant || "default";

  const parts = [];
  let lastIndex = 0;
  let match;

  MENTION_MARKER_REGEX.lastIndex = 0;
  // eslint-disable-next-line no-cond-assign
  while ((match = MENTION_MARKER_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    parts.push(
      renderMentionHighlight(match, `${match.index}-${match[2]}`, variant)
    );

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
};

const isMentionWordChar = (char) => /[A-Za-zÀ-ÿ0-9._'-]/.test(char);
const isMentionNameStart = (char) => /[A-Za-zÀ-ÿ0-9]/.test(char);

export const findPlainMentionRanges = (text) => {
  if (!text) return [];

  const ranges = [];
  let index = 0;

  while (index < text.length) {
    const current = text[index];
    const previous = index > 0 ? text[index - 1] : "";

    if (current !== "@" || (index > 0 && !/\s|\(/.test(previous))) {
      index += 1;
      continue;
    }

    let cursor = index + 1;
    let hasName = false;
    let lastValid = -1;

    while (cursor < text.length) {
      const char = text[cursor];

      if (isMentionWordChar(char)) {
        hasName = true;
        lastValid = cursor;
        cursor += 1;
        continue;
      }

      if (
        char === " " &&
        hasName &&
        cursor + 1 < text.length &&
        isMentionNameStart(text[cursor + 1])
      ) {
        cursor += 1;
        continue;
      }

      break;
    }

    if (!hasName || lastValid < index + 1) {
      index += 1;
      continue;
    }

    ranges.push({
      start: index,
      end: lastValid + 1,
      name: text.slice(index + 1, lastValid + 1)
    });

    index = lastValid + 1;
  }

  return ranges;
};

export const renderPlainMentionText = (text, options = {}) => {
  if (!text) return null;
  const variant = options.variant || "default";
  const ranges = findPlainMentionRanges(text);

  if (!ranges.length) {
    return [text];
  }

  const parts = [];
  let lastIndex = 0;

  ranges.forEach((range, index) => {
    if (range.start > lastIndex) {
      parts.push(text.slice(lastIndex, range.start));
    }

    parts.push(
      renderMentionHighlight(
        [`@${range.name}`, range.name],
        `plain-${range.start}-${index}`,
        variant
      )
    );

    lastIndex = range.end;
  });

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
};

export const extractMentionUserIds = (text) => {
  if (!text) return [];
  const ids = [];
  let match;
  MENTION_MARKER_REGEX.lastIndex = 0;
  // eslint-disable-next-line no-cond-assign
  while ((match = MENTION_MARKER_REGEX.exec(text)) !== null) {
    const id = Number(match[2]);
    if (Number.isFinite(id) && !ids.includes(id)) ids.push(id);
  }
  return ids;
};
